/**
 * ****************************************************************************
 * @description     :   Calendar Helper Functions
 * @created at      :   2025/09/26
 * @created by      :   DaoNX
 * @package         :   Move
 * @copyright       :   Copyright (c) A.N.S Asia
 * @version         :   1.0.0
 * ****************************************************************************
 */

import { isHoliday, between as getHolidaysBetween } from '@holiday-jp/holiday_jp';
import store from '@/store';
import { markRaw } from 'vue';
import ansCalendarDatePickerPopup from '@/components/ansCalendarDatePickerPopup/index.vue';

export const mapScheduleModes = {
    month: { title: 'スケジュール(月)', label: '月', section: 'views', value: 'month' },
    week: { title: 'スケジュール(週)', label: '週', section: 'views', value: 'week' },
    groupA: { title: 'スケジュール(グループA)', label: 'グループA', section: 'groups', value: 'groupA' },
    groupB: { title: 'スケジュール(グループB)', label: 'B', section: 'groups', value: 'groupB' },
    equipmentMonth: { title: 'スケジュール(設備(月))', label: '設備(月)', section: 'equipments', value: 'equipmentMonth', child: true },
    equipmentWeek: { title: 'スケジュール(設備(週))', label: '設備(週)', section: 'equipments', value: 'equipmentWeek', child: true }
};

export const typeEvent = [
    { code: '1', name: 'オフィス', color: '#1976D2' },
    { code: '2', name: '営業', color: '#388E3C' },
    { code: '3', name: '現場', color: '#FFA000' },
    { code: '4', name: '現場応援', color: '#FBC02D' },
    { code: '5', name: '出張', color: '#0288D1' },
    { code: '6', name: '会議', color: '#7B1FA2' },
    { code: '7', name: '打合わせ', color: '#0097A7' },
    { code: '8', name: '面談', color: '#C2185B' },
    { code: '9', name: '来客', color: '#6D4C41' },
    { code: '10', name: '面接', color: '#00897B' },
    { code: '11', name: 'その他', color: '#757575' },
    { code: '12', name: '休み', color: '#E53935' },
    { code: '13', name: '有給', color: '#FFD600' },
    { code: '14', name: '欠勤', color: '#BDBDBD' },
    { code: '15', name: 'モーニング', color: '#F06292' },
    { code: '16', name: '外出', color: '#43A047' },
    { code: '17', name: 'ToDo', color: '#FFA726' }
];

export const groupCheckin = [
    { name: '未', color: '#FF0000' },
    { name: '未', color: '#388E3C' },
    { name: '？', color: '#000000' },
    { name: '△', color: '#FF0000' },
    { name: '〇', color: '#000000' }
];

export function typeEventMap() {
    const map = new Map();
    typeEvent.forEach((item) => {
        map.set(item.code, { color: item.color, name: item.name });
    });
    return map;
}

/**
 * Date and Time Utilities
 */

// Normalize time format from H:M or HH:MM to HH:MM
export function convertTimeFormat(timeString) {
    if (!timeString || typeof timeString !== 'string') return '00:00';

    const parts = timeString.split(':');
    if (parts.length !== 2) return '00:00';

    const hours = parseInt(parts[0]) || 0;
    const minutes = parseInt(parts[1]) || 0;

    // Ensure valid time ranges
    const validHours = Math.max(0, Math.min(24, hours));
    const validMinutes = Math.max(0, Math.min(59, minutes));

    return `${validHours.toString().padStart(2, '0')}:${validMinutes.toString().padStart(2, '0')}`;
}

// Format date with specified format
export function formatDate(date, format) {
    if (!(date instanceof Date) || isNaN(date)) return '';
    if (typeof format !== 'string') return '';

    const map = {
        YYYY: date.getFullYear().toString(),
        YY: date.getFullYear().toString().slice(-2),
        MM: String(date.getMonth() + 1).padStart(2, '0'),
        M: (date.getMonth() + 1).toString(),
        DD: String(date.getDate()).padStart(2, '0'),
        D: date.getDate().toString(),
        HH: String(date.getHours()).padStart(2, '0'),
        H: date.getHours().toString(),
        mm: String(date.getMinutes()).padStart(2, '0'),
        m: date.getMinutes().toString(),
        ss: String(date.getSeconds()).padStart(2, '0'),
        s: date.getSeconds().toString(),
        ddd: date.toLocaleString('ja', { weekday: 'short' }),
        dddd: date.toLocaleString('ja', { weekday: 'long' })
    };

    // Replace all supported tokens in the format string
    return format.replace(/(YYYY|YY|MM|M|DD|D|HH|H|mm|m|ss|s|dddd|ddd)/g, (token) => (map[token] !== undefined ? map[token] : token));
}

// Check if two dates are the same day
export function isSameDate(date1, date2) {
    return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
}

// Check if a day object represents today
export function isDayToday(day) {
    const today = new Date();
    const dayDate = new Date(day.fullDate);
    return today.getFullYear() === dayDate.getFullYear() && today.getMonth() === dayDate.getMonth() && today.getDate() === dayDate.getDate();
}

// Get start of week for a given date
export function getStartOfWeek({ date = new Date(), mode = 'sunday' }) {
    const startDate = new Date(date);
    startDate.setHours(0, 0, 0, 0);

    const day = startDate.getDay();

    let firstDay = 0;
    let startOfWeek = startDate;

    switch (mode) {
        case 'sunday':
            firstDay = 0;
            break;
        case 'monday':
            firstDay = 1;
            break;
        case 'today':
            firstDay = startDate.getDay();
            break;
        default:
            firstDay = 0;
            break;
    }
    const diff = day - firstDay;

    startOfWeek.setDate(startDate.getDate() - diff);
    startOfWeek.setHours(0, 0, 0, 0);

    return startOfWeek;
}

export function getEndOfWeek({ date = new Date(), weeksToShow = 2, mode = 'sunday' }) {
    const endOfWeek = getStartOfWeek({ date, mode: mode });
    endOfWeek.setDate(endOfWeek.getDate() + weeksToShow * 7);
    return endOfWeek;
}

// Get start of month for a given date
export function getStartOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}

// Get end of month for a given date
export function getEndOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

// Get start of month for a given date
export function getCalendarMonthStart(date) {
    const startOfMonth = getStartOfMonth(date);
    return getStartOfWeek({ date: startOfMonth }); // Sunday start
}

// Get end of month for a given date
export function getCalendarMonthEnd(date) {
    const endOfMonth = getEndOfMonth(date);

    const endWeekStart = getStartOfWeek({ date: endOfMonth });

    const endDate = new Date(endWeekStart);
    endDate.setDate(endDate.getDate() + 6); // Saturday (end of week)

    return endDate;
}

// Generate week view days
export function getWeekViewDays({
    currentDate,
    weeksToShow = 1,
    selectedDate = null,
    events = [],
    weekStartMode = 'sunday',
    closingDays = [],
    incorporationDate = ''
}) {
    const days = [];
    const startDate = getStartOfWeek({ date: currentDate, mode: weekStartMode });
    const totalDays = weeksToShow * 7;

    const eventsWithIndex = events.map((event, eventIndex) => {
        return {
            ...event,
            eventIndex: eventIndex
        };
    });

    for (let i = 0; i < totalDays; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);

        const dayObj = createDayObject(date, selectedDate, closingDays, incorporationDate);

        dayObj.events = eventsWithIndex.filter((event) => {
            const eventDate = new Date(event.date);
            return isSameDate(eventDate, date);
        });

        const checkinIndex = eventsWithIndex.find((event) => {
            const eventDate = new Date(event.date);
            return isSameDate(eventDate, date);
        })?.checkin;

        dayObj.checkin = groupCheckin[checkinIndex];

        days.push(dayObj);
    }

    return days;
}

// Generate month view days
export function getMonthViewDays({ currentDate, selectedDate = null, events = [], closingDays = [], incorporationDate = '' }) {
    const days = [];
    const startOfMonth = getStartOfMonth(currentDate);
    const endOfMonth = getEndOfMonth(currentDate);
    const eventsWithIndex = events.map((event, eventIndex) => {
        return {
            ...event,
            eventIndex: eventIndex
        };
    });

    // Start from the beginning of the week containing the first day
    const startDate = getStartOfWeek({ date: startOfMonth });

    // Calculate the end of the week containing the last day of the month
    const endDate = getStartOfWeek({ date: endOfMonth });
    endDate.setDate(endDate.getDate() + 6);

    // Calculate total days needed dynamically
    const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

    for (let i = 0; i < totalDays; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);

        const dayObj = createDayObject(date, selectedDate, closingDays, incorporationDate);

        dayObj.isOtherMonth = date.getMonth() !== currentDate.getMonth();

        dayObj.events = eventsWithIndex.filter((event) => {
            const eventDate = new Date(event.date);
            return isSameDate(eventDate, date);
        });

        const checkinIndex = eventsWithIndex.find((event) => {
            const eventDate = new Date(event.date);
            return isSameDate(eventDate, date);
        })?.checkin;

        dayObj.checkin = groupCheckin[checkinIndex];

        days.push(dayObj);
    }

    return days;
}

// Format date for popup display
export function formatDateForPopup(date) {
    return formatDate(date, 'YYYY年MM月DD日') + '(' + date.toLocaleString('ja', { weekday: 'short' }) + ')';
}

// Smart end date formatting
export function formatSmartEndDate(startDate, endDate) {
    if (startDate.getFullYear() !== endDate.getFullYear()) {
        return formatDate(endDate, 'YYYY年MM月DD日');
    } else if (startDate.getMonth() !== endDate.getMonth()) {
        return formatDate(endDate, 'MM月DD日');
    } else {
        return formatDate(endDate, 'DD日');
    }
}

// Format date for display (MM月DD日(曜日))
export function formatDateDisplay(date) {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const weekday = date.toLocaleString('ja', { weekday: 'short' });
    return `${month}月${day}日(${weekday})`;
}

/**
 * Event Utilities
 */

// Get event display text with time range
export function getEventDisplayText(event) {
    if (!event) {
        return '';
    }

    if (event.isFullDay) {
        return `終日 ${event.eventName || ''}`;
    } else if (event.startTime && event.endTime) {
        return `${convertTimeFormat(event.startTime) + '～' + convertTimeFormat(event.endTime)} ${event.eventName || ''}`;
    } else if (event.startTime) {
        return `${event.startTime} ${event.eventName || ''}`;
    } else {
        return event.eventName || '';
    }
}

// Check if tooltip should be shown for event
export function shouldShowTooltip(event) {
    if (!event) {
        return false;
    }

    const displayText = getEventDisplayText(event);
    return displayText && displayText.length > 15;
}

// Trigger event handler
// export function triggerEventHandler({ data, eventType }) {
//     if (data.onEvent && data.onEvent[eventType]) {
//         if (typeof data.onEvent[eventType] === 'function') {
//             data.onEvent[eventType](data);
//         } else {
//             console.log(`Event ${eventType}:`, data, data);
//         }
//     }
// }

/**
 * CSS and UI Utilities
 */
// Get CSS classes for day cell
export function getDayCellClasses(day) {
    return {
        'is-today': day.isToday,
        'is-selected': day.isSelected,
        'is-other-month': day.isOtherMonth,
        'is-sunday': day.isSunday,
        'is-saturday': day.isSaturday,
        'is-holiday': day.isHoliday,
        'is-closing-day': day.isClosingDay
    };
}

/**
 * Navigation Utilities
 */

// Execute navigation action
export function executeNavigationAction(context, action, params) {
    const method = context[action];
    if (typeof method === 'function') {
        method.apply(context, params);
    }
}

/**
 * Time Calculation Utilities
 */

// Convert HH:MM to total minutes
export function toMinutes(timeString) {
    const normalizedTime = convertTimeFormat(timeString);
    const [hours, minutes] = normalizedTime.split(':').map(Number);
    return hours * 60 + minutes;
}

// Convert minutes to HH:MM format
export function formatMinutesToHHMM(totalMinutes) {
    // Handle overflow (next day)
    if (totalMinutes >= 24 * 60) {
        totalMinutes = 24 * 60;
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

// Calculate duration between two times in minutes
export function calculateDuration(startTime, endTime) {
    const startMinutes = toMinutes(startTime);
    const endMinutes = toMinutes(endTime);
    return Math.max(15, endMinutes - startMinutes); // Minimum 15 minutes
}

// Calculate end time based on start time and duration
export function calculateEndTime(startTime, durationMinutes = 60) {
    const [hours, minutes] = startTime.split(':').map(Number);
    let endHours = hours;
    let endMinutes = minutes + durationMinutes;

    if (endMinutes >= 60) {
        endHours += Math.floor(endMinutes / 60);
        endMinutes = endMinutes % 60;
    }

    if (endHours >= 24) {
        endHours = 23;
        endMinutes = 45;
    }

    return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
}

/**
 * Event Interaction Utilities
 */

// Handle event interaction (click, hover, etc.)
export function handleEventInteraction({ data, eventType, day = null, emitCallback = null }) {
    // triggerEventHandler({ data, eventType });

    if (emitCallback) {
        if (eventType === 'click') {
            emitCallback('event-click', { data, day });
        } else if (eventType === 'mouseEnter') {
            emitCallback('event-mouse-enter', { data });
        } else if (eventType === 'mouseLeave') {
            emitCallback('event-mouse-leave', { data });
        }
    }
}

// Create day object with common properties
export function createDayObject(date, selectedDate = null, closingDays = [], incorporationDate = '') {
    const isHolidayFlag = isHoliday(date);
    const dateStr = formatDate(date, 'YYYY-MM-DD');
    const closingDayInfo = closingDays.find((d) => d.date === dateStr);

    // Get holiday name using between() - isHoliday() only returns boolean
    let holidayName = null;
    if (isHolidayFlag) {
        const holidays = getHolidaysBetween(date, date);
        if (holidays && holidays.length > 0) {
            holidayName = holidays[0].name;
        }
    }

    // Check if this date matches the incorporation date (MM-DD format)
    let isIncorporationDate = false;
    if (incorporationDate) {
        const monthDay = formatDate(date, 'MM-DD');
        isIncorporationDate = monthDay === incorporationDate;
    }

    return {
        date: date.getDate(),
        fullDate: new Date(date),
        dayOfWeek: date.getDay(),
        isToday: isSameDate(date, new Date()),
        isSelected: selectedDate ? isSameDate(date, selectedDate) : false,
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        isSunday: date.getDay() === 0,
        isSaturday: date.getDay() === 6,
        isHoliday: isHolidayFlag,
        holidayName: holidayName,
        isClosingDay: !!closingDayInfo,
        closingDayName: closingDayInfo?.name || null,
        isIncorporationDate: isIncorporationDate
    };
}

/**
 * Get week number of the month for a given date
 * @param {Object} params - Parameters object
 * @param {Date} params.date - The date to calculate week number for
 * @param {String} params.weekStartMode - 'sunday' (default) or 'monday'
 * @returns {Number} Week number (1-5)
 */
export function getWeekOfMonth({ date, weekStartMode = 'sunday' }) {
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const firstDayWeekday = firstDayOfMonth.getDay(); // 0 = Sunday, 1 = Monday, ...

    // Calculate offset based on week start mode
    let offset;
    if (weekStartMode === 'monday') {
        // Week starts on Monday (1)
        offset = firstDayWeekday === 0 ? 6 : firstDayWeekday - 1;
    } else {
        // Week starts on Sunday (0)
        offset = firstDayWeekday;
    }

    // Calculate week number
    return Math.ceil((date.getDate() + offset) / 7);
}

/**
 * Navigation Methods
 */

// Navigate by date
export function navigateDate(currentState, numberDate = 1, viewType = 'week', weeksToShow = 1) {
    const newSelectedDate = new Date(currentState.selectedDate.getTime() + numberDate * 24 * 60 * 60 * 1000);

    if (viewType === 'day') {
        // For day view, always move current date with selected date
        currentState.currentDate = new Date(newSelectedDate);
    } else if (viewType === 'week') {
        // Check if new date is outside current week range
        const startOfCurrentWeek = getStartOfWeek({ date: currentState.currentDate });
        const endOfCurrentWeek = new Date(startOfCurrentWeek.getTime() + (weeksToShow * 7 - 1) * 24 * 60 * 60 * 1000);

        if (newSelectedDate < startOfCurrentWeek || newSelectedDate > endOfCurrentWeek) {
            // Move calendar to show the new selected date
            currentState.currentDate = new Date(newSelectedDate);
        }
    } else {
        // For month view, check if we need to change month
        if (
            newSelectedDate.getMonth() !== currentState.currentDate.getMonth() ||
            newSelectedDate.getFullYear() !== currentState.currentDate.getFullYear()
        ) {
            currentState.currentDate = new Date(newSelectedDate.getFullYear(), newSelectedDate.getMonth(), 1);
        }
    }

    currentState.selectedDate = newSelectedDate;
    return currentState;
}

// Navigate by week
export function navigateWeek(currentState, numberWeek = 1) {
    const days = numberWeek * 7;
    currentState.currentDate = new Date(currentState.currentDate.getTime() + days * 24 * 60 * 60 * 1000);
    currentState.selectedDate = new Date(currentState.selectedDate.getTime() + days * 24 * 60 * 60 * 1000);
    return currentState;
}

// Navigate by month
export function navigateMonth(currentState, numberMonth = 1) {
    const newCurrentDate = new Date(currentState.currentDate);
    newCurrentDate.setMonth(newCurrentDate.getMonth() + numberMonth);
    currentState.currentDate = newCurrentDate;

    const newSelectedDate = new Date(currentState.selectedDate);
    newSelectedDate.setMonth(newSelectedDate.getMonth() + numberMonth);
    currentState.selectedDate = newSelectedDate;

    return currentState;
}

// Navigate by year
export function navigateYear(currentState, numberYear = 1) {
    const newCurrentDate = new Date(currentState.currentDate);
    newCurrentDate.setFullYear(newCurrentDate.getFullYear() + numberYear);
    currentState.currentDate = newCurrentDate;

    const newSelectedDate = new Date(currentState.selectedDate);
    newSelectedDate.setFullYear(newSelectedDate.getFullYear() + numberYear);
    currentState.selectedDate = newSelectedDate;

    return currentState;
}

// Go to today
export function goToday(currentState) {
    const today = new Date();
    currentState.currentDate = today;
    currentState.selectedDate = today;
    return currentState;
}

/**
 * Date String Parsing Utilities
 */

// Parse date string from YYYYMMDD or YYYY/MM/DD format to Date object
export function parseDateString({ dateString, defaultDate = null }) {
    if (!dateString || typeof dateString !== 'string') {
        return defaultDate || new Date();
    }

    // Remove all slashes to normalize format
    const cleanDate = dateString.replace(/\//g, '');

    // Check if format is YYYYMMDD (8 characters)
    if (cleanDate.length === 8) {
        const year = parseInt(cleanDate.substring(0, 4));
        const month = parseInt(cleanDate.substring(4, 6)) - 1; // Month is 0-indexed
        const day = parseInt(cleanDate.substring(6, 8));

        // Validate parsed values
        if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
            return new Date(year, month, day);
        }
    }

    return defaultDate || new Date();
}

//Add days to a date string and return formatted result
export function addDaysToDateString({ baseDate, offset = 0, format = 'YYYY-MM-DD' }) {
    // Add offset days
    baseDate.setDate(baseDate.getDate() + offset);

    // Default: YYYY-MM-DD
    return formatDate(baseDate, format);
}

/**
 * Add days to a Date object and return new Date object
 * @param {Date} date - Base date
 * @param {Number} days - Number of days to add (can be negative)
 * @returns {Date} New Date object with days added
 */
export function addDays(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

/**
 * Global Wrapper Utilities
 */

// Handle calendar title click using global wrapper
export function handleCalendarTitleClick(event, currentDate, onDateChange) {
    event.preventDefault();
    event.stopPropagation();

    const triggerElement = event.currentTarget;

    // Show date picker using global wrapper
    store.commit('app/showGlobalWrapper', {
        component: markRaw(ansCalendarDatePickerPopup),
        componentProps: {
            visible: true,
            initialDate: currentDate || new Date()
        },
        triggerElement: triggerElement,
        onClose: null,
        onSelect: onDateChange
    });
}
